import {
  AnchorButton as BlueprintAnchorButton,
  AnchorButtonProps as BlueprintAnchorButtonProps,
  Collapse as BlueprintCollapse,
  CollapseProps as BlueprintCollapseProps,
} from '@blueprintjs/core';
import { ChevronDown, ChevronRight, ChevronUp } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import {
  InteractiveBadge,
  InteractiveBadgeProps,
} from 'bones/design-system/components/badge/InteractiveBadge';
import {
  Icon,
  IconComponentType,
  IconWrapperProps,
} from 'bones/design-system/components/icons/Icon';

const BADGE_SIZE_BY_ACCORDION_SIZE: Record<
  AccordionProps['size'],
  InteractiveBadgeProps['size']
> = {
  md: 'md',
  sm: 'sm',
};

const ICON_SIZE_BY_ACCORDION_SIZE: Record<
  AccordionProps['size'],
  IconWrapperProps['size']
> = {
  md: 'md',
  sm: 'sm',
};

export type AccordionProps = BlueprintCollapseProps &
  Pick<BlueprintAnchorButtonProps, 'disabled'> & {
    'data-testid'?: never;
    actions?: React.ReactNode;
    badgeProps?: Omit<
      InteractiveBadgeProps,
      'active' | 'disabled' | 'selected' | 'size' | 'variant'
    >;
    children?: React.ReactNode;
    className?: string;
    icon?: 'left' | 'right';
    iconTestId?: string;
    initialIsOpen?: boolean;
    isOpen?: boolean;
    label?: string;
    onInteraction?: (
      nextIsOpen: boolean,
      evt?: React.SyntheticEvent<HTMLElement>
    ) => void;
    size: 'md' | 'sm';
    testId?: string;
  };

export const Accordion: React.VFC<AccordionProps> = ({
  actions,
  badgeProps,
  children,
  className,
  disabled,
  icon,
  iconTestId,
  initialIsOpen = false,
  isOpen: controlledIsOpen,
  label,
  onInteraction,
  size,
  testId,
  ...props
}) => {
  const [localIsOpen, setLocalIsOpen] = React.useState(initialIsOpen);
  const isOpen = controlledIsOpen ?? localIsOpen;

  let leftIcon: IconComponentType | undefined;
  let rightIcon: IconComponentType | undefined;
  if (icon === 'left' && size === 'md') {
    leftIcon = isOpen ? ChevronUp : ChevronDown;
  } else if (icon === 'left' && size === 'sm') {
    leftIcon = isOpen ? ChevronDown : ChevronRight;
  } else if (icon === 'right' && size === 'md') {
    rightIcon = isOpen ? ChevronUp : ChevronDown;
  }

  const handleClick = (evt: React.MouseEvent<HTMLElement>) => {
    onInteraction?.(!isOpen, evt);
    if (controlledIsOpen === undefined) {
      setLocalIsOpen(!isOpen);
    }
  };

  return (
    <div
      className={classNames(
        className,
        'vb-accordion',
        `vb-accordion-size--${size}`
      )}
    >
      <div className="vb-accordion-header">
        <BlueprintAnchorButton
          className="vb-accordion-button"
          data-testid={testId}
          disabled={disabled}
          onClick={handleClick}
        >
          {leftIcon ? (
            <Icon
              IconComponent={leftIcon}
              size={ICON_SIZE_BY_ACCORDION_SIZE[size]}
            />
          ) : null}
          <div className="vb-accordion-text">
            {label}
            {badgeProps ? (
              <InteractiveBadge
                disabled={disabled}
                size={BADGE_SIZE_BY_ACCORDION_SIZE[size]}
                variant="light"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...badgeProps}
              />
            ) : null}
          </div>
        </BlueprintAnchorButton>
        <div className="vb-accordion-actions">
          {actions}
          {rightIcon ? (
            <BlueprintAnchorButton
              className="vb-accordion-button"
              data-testid={iconTestId}
              disabled={disabled}
              onClick={handleClick}
            >
              <Icon
                IconComponent={rightIcon}
                size={ICON_SIZE_BY_ACCORDION_SIZE[size]}
              />
            </BlueprintAnchorButton>
          ) : null}
        </div>
      </div>
      <BlueprintCollapse
        isOpen={isOpen}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {children}
      </BlueprintCollapse>
    </div>
  );
};
